import type { ErrorEvent } from '@sentry/nextjs';

// Error patterns to completely ignore (transient/expected errors)
//
// IMPORTANT: do NOT add patterns matching Postgres-outage symptoms here.
// Outage symptom classes (forbidden in this list) include: upstream fetch
// failures, dropped/reset/closed connections, pool-saturation messages,
// reserved-slot warnings, Postgres temporarily-unavailable signals, and
// excessive-connection errors. The browser sees these as Supabase REST
// calls returning HTML 5xx bodies — silencing them hides Supabase Postgres
// outages from Sentry. See
// `docs/architecture/resilience-to-supabase-outages.md` (Layer 5).
// Enforcement: critical-rules-supabase-outage-resilience clause.
const IGNORED_ERROR_PATTERNS = [
  // Transient user-network errors (browser-side noise, NOT outage signals)
  /load failed/i,
  /network request failed/i,
  /network error/i,
  /abort/i,
  // Firefox's dialect of the same benign fetch-abort class above. Firefox
  // surfaces an interrupted response-body stream (e.g. an in-flight Next.js
  // RSC prefetch cancelled when the user navigates away) as
  // `TypeError: Error in input stream` — the nsIInputStream wording — instead
  // of Chrome's "aborted" / Safari's "Load failed". It slipped through because
  // none of the patterns above match that exact phrasing (BALLEE-D2 / BAL-656).
  // Confirmed browser noise, not a defect: Firefox-only across 2 OSes and 2
  // unrelated routes (/home/artist/profile/setup, /:handle), no app stack
  // frames, caught by the app error boundary, preceded by aborted `_rsc=`
  // prefetch fetches. This is NOT a Supabase-outage symptom — an outage is
  // cross-browser and returns an HTML 5xx body, which the
  // cloudflare-origin-timeout fingerprint in `beforeSend` catches and KEEPS before this
  // ignore-list runs. Same BAL-355 benign-browser-noise direction.
  /error in input stream/i,
  // Browser-specific transient errors
  /could not load "util"/i,
  /resizeobserver loop/i,
  /script error/i,
  /non-error promise rejection/i,
  /object not found/i,
  // React hydration errors (often transient)
  /hydration failed/i,
  /rendered more hooks/i,
  /rendered fewer hooks/i,
  // DOM manipulation errors (usually browser extensions interfering)
  /removeChild.*not a child/i,
  /insertBefore.*not a child/i,
  /the node to be removed is not a child/i,
  /the node before which.*is not a child/i,
  /cannot read properties of null.*removeChild/i,
  /cannot read properties of null.*insertBefore/i,
  // Browser-extension / injected-script noise: `domainName` appears NOWHERE in
  // our source (verified). Single-event, zero-users TypeErrors on /:handle where
  // an injected script reads `e.domainName` on a null object — minified frame
  // attributed to the page URL (app:///<handle>), not a real file. Covers both
  // WebKit ("null is not an object") and Chrome ("cannot read properties") (BAL-273).
  /null is not an object.*\.domainName/i,
  /cannot read propert.*null.*\bdomainName\b/i,
  // Maximum call stack errors (often infinite loop in extensions or transient)
  /maximum call stack size exceeded/i,
  // Expected validation errors (business logic)
  /dancer is already cast/i,
  /already assigned/i,
  /scheduling conflict/i,
  // Auth validation errors (expected user input errors)
  /new password should be different from the old password/i,
  /password is too weak/i,
  // GoTrue's AuthWeakPasswordError message ("Password should be at least N
  // characters."). The /password is too weak/ pattern above does NOT match
  // this server-generated wording, so weak-password rejections on
  // /auth/sign-up slipped through as unhandled-rejection noise
  // (BALLEE-F7 / BALLEE-F9, BAL-576). \d+ keeps it robust to the configured
  // minimum length. Also guarded by name in `beforeSend` for defense-in-depth.
  /password should be at least \d+ character/i,
  /user already registered/i,
  /email already in use/i,
  // Rate limiting (expected security feature)
  /rate limit/i,
  /you can only request this after/i,
  /too many requests/i,
  /email rate limit exceeded/i,
  // Supabase Auth rate limiting
  /for security purposes, you can only request this after/i,
  // RLS access denied in admin context (stale session, UI handles gracefully)
  /access denied by security policy/i,
  // Tesseract.js OCR worker error — expected when image can't be decoded (HEIC, corrupt files, etc.)
  // Worker onerror fires independently of the Promise chain, bypassing the try-catch in use-ocr-extraction.ts
  /error attempting to read image/i,
  // Vue.js / Vue Router noise from third-party widgets and webview-injected SDKs
  // (Instagram/Facebook/TikTok in-app browsers, affiliate scripts). We are React/Next.js;
  // any stack frame from compiled Vue is foreign code we cannot fix.
  /avoided redundant navigation to current location/i,
  /cannot read properties of undefined \(reading 'styleClasses'\)/i,
  // IAB (Interactive Advertising Bureau) in-app browser SDK noise — fires from
  // Android/iOS webviews when users open Ballee links from social-media ads.
  /error invoking enabledidusertypeonkeyboardlogging/i,
  /java object is gone/i,
  // IDBDatabase transient — fires when the user closes a tab mid-operation or a
  // browser extension tears down its IndexedDB during page navigation.
  /idbdatabase.*the database connection is closing/i,
  /the database connection is closing/i,
  // Turbopack chunk-load failure after a deploy: a cached page asks for a
  // chunk hash the new release no longer serves. Turbopack's runtime phrases
  // it `Failed to load chunk <path> from module <id>` — NOT webpack's
  // `Loading chunk N failed` / `ChunkLoadError`, which is all Sentry's
  // org-level `chunk-load-error` inbound filter keys on. With that filter ON,
  // two console-message events (no exception, so no type to match) still
  // landed on / and flow.ballee.co/ (BALLEE-HC / BALLEE-H7, BAL-979 / BAL-980).
  // `apps/web/components/chunk-error-handler.tsx` already auto-reloads this
  // class, so the only user impact is one reload. Anchored on the full
  // Turbopack sentence (`from module`) so it cannot swallow `Failed to fetch`
  // or any other load failure. Dropping it here, rather than relying on the
  // inbound filter, is what records it in the BAL-856 drop telemetry under a
  // stable pattern slug.
  /failed to load chunk .* from module \d+/i,
  // @supabase/auth-js `lib/locks.js` (navigatorLock) console.warn on browsers
  // whose `navigator.locks.request` returns a null lock (Mobile Safari 18.5 /
  // iOS on BALLEE-HE). The SDK logs it and then runs the callback anyway —
  // "pretend the lock is acquired ... and just run the function" — so auth
  // is unaffected. Sibling of BALLEE-BT ("Acquiring an exclusive Navigator
  // LockManager lock ... immediately failed", ignored 2026-05-10 as a
  // multi-tab token-lock race the SDK recovers from). The tails are the real
  // anchors, not the `Navigator LockManager` prefix: the same helper also
  // says `returned null lock even with steal: true` (not covered, no
  // evidence yet), and our own `packages/supabase/src/clients/browser-client.ts`
  // throws `Acquiring an exclusive Navigator LockManager lock "<name>" was
  // aborted: page lifecycle state changed` (BAL-792). Neither pattern below
  // matches that first-party one — note it is already caught by the bare
  // `/abort/i` above, a pre-existing client-only gap the server list closed
  // under BAL-891 — so do not loosen these tails (BAL-982).
  /navigator lockmanager returned a null lock/i,
  /acquiring an exclusive navigator lockmanager lock .* immediately failed/i,
];

// External script paths to filter (browser extensions, third-party widgets)
const EXTERNAL_SCRIPT_PATHS = [
  'api_utils.js',
  'chrome-extension://',
  'moz-extension://',
  // MetaMask browser extension injects this script into every page
  'app:///scripts/inpage.js',
  // IAB in-app browser SDK (Instagram/Facebook/TikTok webviews). The scheme is
  // injected by the host app, never by us — anything from this origin is foreign.
  'iabjs://',
];

// Return the first matching ignore pattern's source (a stable slug for
// telemetry), or null when the error should be kept.
export function findIgnoredPattern(errorValue: string): string | null {
  for (const pattern of IGNORED_ERROR_PATTERNS) {
    if (pattern.test(errorValue)) {
      return pattern.source;
    }
  }
  return null;
}

/**
 * The entries of `IGNORED_ERROR_PATTERNS` that may also suppress a RECOVERED
 * console message (BAL-960), named by `RegExp.source`.
 *
 * The full list is tuned for `Error.message` from third-party code, and several
 * of its entries are bare substrings: `/abort/i`, `/network error/i`, `/rate
 * limit/i`, `/access denied by security policy/i`, `/already assigned/i`. Our
 * own structured log lines are prose, so they match those substrings by
 * coincidence. `console.error({ msg: 'Checkout aborted: card-decline mapping
 * missing' })` is a defect report, not an aborted fetch.
 *
 * Before BALLEE-GZ a console capture arrived as `[object Object]`, matched
 * nothing, and was always kept. Recovery must make the message LEGIBLE without
 * making it FILTERABLE by a list that never expected it, or a readability fix
 * quietly becomes a suppression policy.
 *
 * What belongs here: patterns that name their own browser or runtime
 * mechanism, which cannot collide with a sentence a developer wrote. The
 * Turbopack chunk-load sentence is included because BAL-979 found the org-level
 * inbound filter does not match it, and the recovered form is exactly how it
 * arrives (see console-object-message-recovery.test.ts).
 *
 * Selecting by source rather than keeping a second regex list means a drop
 * through either path reports the SAME telemetry slug.
 */
const RECOVERED_CONSOLE_IGNORE_SOURCES = new Set([
  'resizeobserver loop',
  'maximum call stack size exceeded',
  'non-error promise rejection',
  'hydration failed',
  'rendered more hooks',
  'rendered fewer hooks',
  'removeChild.*not a child',
  'insertBefore.*not a child',
  'the node to be removed is not a child',
  'the node before which.*is not a child',
  'failed to load chunk .* from module \\d+',
]);

const RECOVERED_CONSOLE_IGNORE_PATTERNS = IGNORED_ERROR_PATTERNS.filter(
  (pattern) => RECOVERED_CONSOLE_IGNORE_SOURCES.has(pattern.source),
);

// Same contract as findIgnoredPattern, restricted to the patterns that are safe
// against our own prose. Use it for text recovered from a console capture.
export function findRecoveredConsoleIgnoredPattern(
  recoveredMessage: string,
): string | null {
  for (const pattern of RECOVERED_CONSOLE_IGNORE_PATTERNS) {
    if (pattern.test(recoveredMessage)) {
      return pattern.source;
    }
  }
  return null;
}

export function isExternalScriptError(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  return frames.some((frame) =>
    EXTERNAL_SCRIPT_PATHS.some((path) => frame?.filename?.includes(path)),
  );
}
